import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import type { SmartLinkOnboardingResponse } from "../../lib/api/types";

export interface SmartLinkOnboardingCardProps {
  onboarding: SmartLinkOnboardingResponse;
  /** Called after the account is created and the session is established. */
  onLinked: () => void;
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span className={formStyles.label} style={{ fontSize: 11 }}>{label}</span>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}

/**
 * Shown instead of the normal login/register result when the email matches
 * an unlinked Smart Link claim (see auth.service.ts validateUser/register).
 * Review-only by design — no edit fields, no bypass; see the plan notes in
 * lion-energy-user-service/src/services/auth.service.ts.
 */
export function SmartLinkOnboardingCard({ onboarding, onLinked }: SmartLinkOnboardingCardProps) {
  const { t } = useI18n();
  const { confirmSmartLinkOnboarding } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    const result = await confirmSmartLinkOnboarding(onboarding.ticket);
    if (result.success) {
      onLinked();
    } else {
      setError(result.error || t("account.smartLink.onboarding.error"));
      setLoading(false);
    }
  };

  const fullName = [onboarding.preview.firstName, onboarding.preview.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <h1 className={authText.titleLg}>{t("account.smartLink.onboarding.title")}</h1>
      <p className={authText.subtitleCentered}>
        {t("account.smartLink.onboarding.subtitle")}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 20, marginBottom: 20 }}>
        {fullName && <ProfileRow label={t("account.smartLink.onboarding.fieldName")} value={fullName} />}
        <ProfileRow label={t("account.smartLink.onboarding.fieldEmail")} value={onboarding.preview.email} />
        {onboarding.preview.phone && (
          <ProfileRow label={t("account.smartLink.onboarding.fieldPhone")} value={onboarding.preview.phone} />
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      )}

      <Button
        type="button"
        color="primary"
        variant="solid"
        fullWidth
        size="lg"
        loading={loading}
        onClick={handleConfirm}
      >
        {t("account.smartLink.onboarding.confirm")}
      </Button>
    </>
  );
}
